// src/components/LanguagePicker.jsx
// First-visit language chooser. The choice is remembered (localStorage + cookie),
// so it never appears again. Mounted once inside <Router> in App.js.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "../i18n";
import { setApiLang } from "../lib/api";
import { withLangPrefix, stripLangPrefix, SUPPORTED_LANGS } from "../lib/i18nRoutes";
import { hasChosenLang, markLangChosen, onLangChosen } from "../lib/langChoice";

const OPTIONS = [
  { code: "en", label: "English", sub: "Continue in English" },
  { code: "fr", label: "Français", sub: "Continuer en français" },
  { code: "de", label: "Deutsch", sub: "Auf Deutsch fortfahren" },
  { code: "ar", label: "العربية", sub: "المتابعة بالعربية" },
];

function browserLang() {
  try {
    const s = (navigator.language || "en").toLowerCase().split(/[-_]/)[0];
    return SUPPORTED_LANGS.includes(s) ? s : "en";
  } catch {
    return "en";
  }
}

export default function LanguagePicker() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { setLang } = useTranslation();
  const firstBtn = useRef(null);
  const [open, setOpen] = useState(() => !hasChosenLang());

  const urlLang = stripLangPrefix(pathname).lang; // null when no /xx prefix
  const suggested = urlLang || browserLang();

  useEffect(() => onLangChosen(() => setOpen(!hasChosenLang())), []);

  const isAdmin = pathname.startsWith("/admin");
  const visible = open && !isAdmin;

  // lock scroll, focus, Esc = keep suggested language
  const choose = useCallback(
    (code) => {
      try { setLang(code); } catch {}
      try { setApiLang(code); } catch {}
      markLangChosen(code);
      try {
        window.dispatchEvent(new CustomEvent("site:language_changed", { detail: { lang: code } }));
      } catch {}
      setOpen(false);

      // stay on the same page, swap only the language prefix
      const { lang: current, rest } = stripLangPrefix(pathname);
      if (current && current !== code) {
        navigate(withLangPrefix(rest || "/", code) + window.location.search, { replace: true });
      }
    },
    [navigate, pathname, setLang]
  );

  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    firstBtn.current?.focus();
    const onKey = (e) => e.key === "Escape" && choose(suggested);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [visible, choose, suggested]);

  if (!visible) return null;

  return (
    <div className="lp-backdrop" role="presentation">
      <style>{CSS}</style>
      <div className="lp-card" role="dialog" aria-modal="true" aria-labelledby="lp-title">
        <div className="lp-globe" aria-hidden>🌐</div>
        <h2 id="lp-title" className="lp-title">
          Choose your language
          <span className="lp-title-sub">Choisissez votre langue · Sprache wählen · اختر لغتك</span>
        </h2>
        <div className="lp-grid">
          {OPTIONS.map((o, i) => (
            <button
              key={o.code}
              ref={i === 0 ? firstBtn : null}
              type="button"
              className={`lp-opt${o.code === suggested ? " lp-opt--suggested" : ""}`}
              dir={o.code === "ar" ? "rtl" : "ltr"}
              lang={o.code}
              onClick={() => choose(o.code)}
            >
              <span className="lp-opt-label">{o.label}</span>
              <span className="lp-opt-sub">{o.sub}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const CSS = `
.lp-backdrop{
  position:fixed;inset:0;z-index:2147483001;
  display:flex;align-items:center;justify-content:center;padding:16px;
  background:rgba(0,0,0,.6);backdrop-filter:blur(3px);
  font-family:'DM Sans','Noto Sans Arabic',system-ui,sans-serif;
}
.lp-card{
  width:100%;max-width:460px;background:#fff;color:#111;border-radius:14px;
  padding:26px 22px 22px;text-align:center;box-shadow:0 20px 60px rgba(0,0,0,.4);
}
.lp-globe{font-size:34px;line-height:1;margin-bottom:8px;}
.lp-title{font-size:20px;font-weight:700;margin:0 0 18px;}
.lp-title-sub{display:block;font-size:12px;font-weight:400;color:#666;margin-top:6px;}
.lp-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
.lp-opt{
  cursor:pointer;background:#f6f6f6;border:2px solid transparent;border-radius:10px;
  padding:14px 10px;font:inherit;color:inherit;display:flex;flex-direction:column;gap:2px;
  transition:border-color .15s, background .15s;
}
.lp-opt:hover,.lp-opt:focus-visible{border-color:#e11d2e;background:#fff;outline:none;}
.lp-opt--suggested{border-color:#ddd;background:#fff;}
.lp-opt-label{font-size:17px;font-weight:700;}
.lp-opt-sub{font-size:12px;color:#666;}
@media(max-width:380px){.lp-grid{grid-template-columns:1fr;}}
`;
